import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { LeadershipTermsManageDialog } from '@/components/shared/leadership-terms-manage-dialog';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { PageHeader } from '@/components/shared/page-header';
import { Tip } from '@/components/shared/tip';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { SwitchField } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { LeadershipSubnav } from '@/pages/people/leadership-subnav';

const CUSTOM_ROLE = '__custom__';

interface CouncilTerm {
  id: string;
  startYear: number;
  endYear: number | null;
  label: string | null;
  isCurrent: boolean;
  officeCount: number;
}

interface CouncilOffice {
  id: string;
  memberId: string;
  memberName: string;
  roleCode: string | null;
  roleName: string;
  sortOrder: number;
}

interface OfficerRole {
  code: string;
  label: string;
  sortOrder: number;
}

interface MemberOption {
  id: string;
  firstName: string;
  lastName: string;
}

const formatTermYears = (term: Pick<CouncilTerm, 'startYear' | 'endYear'>): string => {
  if (term.endYear == null || term.startYear === term.endYear) {
    return String(term.startYear);
  }
  return `${term.startYear}–${term.endYear}`;
};

const formatTermLabel = (
  term: Pick<CouncilTerm, 'startYear' | 'endYear' | 'label' | 'isCurrent'>,
  currentBadge: string,
): string => {
  const years = term.label?.trim() || formatTermYears(term);
  return term.isCurrent ? `${years} (${currentBadge})` : years;
};

export const CouncilPage = () => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const canManage = hasPermission(membership, PERMISSIONS.PEOPLE_MANAGE_COUNCIL);

  const [selectedTermId, setSelectedTermId] = useState<string | null>(null);
  const [termsManageOpen, setTermsManageOpen] = useState(false);
  const [termOpen, setTermOpen] = useState(false);
  const [editingTermId, setEditingTermId] = useState<string | null>(null);
  const [officeOpen, setOfficeOpen] = useState(false);
  const [editingOffice, setEditingOffice] = useState<CouncilOffice | null>(null);
  const [removeOfficeId, setRemoveOfficeId] = useState<string | null>(null);
  const [startYear, setStartYear] = useState(String(new Date().getFullYear()));
  const [endYear, setEndYear] = useState(String(new Date().getFullYear()));
  const [termLabel, setTermLabel] = useState('');
  const [termIsCurrent, setTermIsCurrent] = useState(false);
  const [memberId, setMemberId] = useState<string | null>(null);
  const [selectedMember, setSelectedMember] = useState<{ id: string; label: string } | null>(
    null,
  );
  const [memberQuery, setMemberQuery] = useState('');
  const [roleSelection, setRoleSelection] = useState<string | null>(null);
  const [customRoleName, setCustomRoleName] = useState('');

  const terms = useQuery({
    queryKey: ['people', 'council', 'terms'],
    queryFn: () =>
      api.get<{ data: CouncilTerm[] }>('/people/council/terms?pageSize=100'),
  });

  const roles = useQuery({
    queryKey: ['people', 'council', 'officer-roles'],
    queryFn: () =>
      api.get<{ data: OfficerRole[] }>('/people/council/officer-roles'),
  });

  const members = useQuery({
    queryKey: ['people', 'members', 'council-picker', memberQuery],
    queryFn: () =>
      api.get<{ data: MemberOption[] }>(
        `/people/members?pageSize=50&status=active${
          memberQuery ? `&q=${encodeURIComponent(memberQuery)}` : ''
        }`,
      ),
    enabled: officeOpen,
  });

  const isEditingOffice = Boolean(editingOffice);
  const termRows = terms.data?.data ?? [];

  const openAssignOffice = () => {
    setEditingOffice(null);
    setOfficeOpen(true);
  };

  const openEditOffice = (office: CouncilOffice) => {
    setEditingOffice(office);
    setOfficeOpen(true);
  };

  const closeOfficeDialog = () => {
    setOfficeOpen(false);
    setEditingOffice(null);
  };

  useEffect(() => {
    if (selectedTermId && termRows.some((term) => term.id === selectedTermId)) return;
    const current = termRows.find((term) => term.isCurrent);
    setSelectedTermId(current?.id ?? termRows[0]?.id ?? null);
  }, [selectedTermId, termRows]);

  const offices = useQuery({
    queryKey: ['people', 'council', 'terms', selectedTermId, 'offices'],
    queryFn: () =>
      api.get<{ data: CouncilOffice[] }>(
        `/people/council/terms/${selectedTermId}/offices?pageSize=100`,
      ),
    enabled: Boolean(selectedTermId),
  });

  useEffect(() => {
    if (!termOpen) return;
    if (editingTermId) {
      const term = termRows.find((row) => row.id === editingTermId);
      if (!term) return;
      setStartYear(String(term.startYear));
      setEndYear(term.endYear != null ? String(term.endYear) : String(term.startYear));
      setTermLabel(term.label ?? '');
      setTermIsCurrent(term.isCurrent);
      return;
    }
    const year = String(new Date().getFullYear());
    setStartYear(year);
    setEndYear(year);
    setTermLabel('');
    setTermIsCurrent(!termRows.some((term) => term.isCurrent));
  }, [termOpen, editingTermId, termRows]);

  useEffect(() => {
    if (!officeOpen) return;
    if (editingOffice) {
      setMemberId(editingOffice.memberId);
      setSelectedMember({ id: editingOffice.memberId, label: editingOffice.memberName });
      setMemberQuery('');
      if (editingOffice.roleCode) {
        setRoleSelection(editingOffice.roleCode);
        setCustomRoleName('');
      } else {
        setRoleSelection(CUSTOM_ROLE);
        setCustomRoleName(editingOffice.roleName);
      }
      return;
    }
    setMemberId(null);
    setSelectedMember(null);
    setMemberQuery('');
    setRoleSelection(null);
    setCustomRoleName('');
  }, [officeOpen, editingOffice]);

  const openCreateTerm = () => {
    setEditingTermId(null);
    setTermOpen(true);
  };

  const openEditTerm = (termId?: string) => {
    const id = termId ?? selectedTermId;
    if (!id) return;
    setSelectedTermId(id);
    setEditingTermId(id);
    setTermOpen(true);
  };

  const openCreateTermFromManage = () => {
    setTermsManageOpen(false);
    openCreateTerm();
  };

  const openEditTermFromManage = (termId: string) => {
    setTermsManageOpen(false);
    openEditTerm(termId);
  };

  const closeTermDialog = () => {
    setTermOpen(false);
    setEditingTermId(null);
  };

  const saveTerm = useMutation({
    mutationFn: () => {
      const body = {
        startYear: Number(startYear),
        endYear: termIsCurrent ? null : Number(endYear),
        label: termLabel.trim() || null,
        isCurrent: termIsCurrent,
      };
      if (editingTermId) {
        return api.patch<CouncilTerm>(`/people/council/terms/${editingTermId}`, body);
      }
      return api.post<CouncilTerm>('/people/council/terms', body);
    },
    onSuccess: (term) => {
      toast.success(t('council.termSaved'));
      closeTermDialog();
      setSelectedTermId(term.id);
      void queryClient.invalidateQueries({ queryKey: ['people', 'council', 'terms'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const assignOffice = useMutation({
    mutationFn: () => {
      if (!selectedTermId || !memberId || !roleSelection) {
        throw new Error('incomplete');
      }
      const isCustom = roleSelection === CUSTOM_ROLE;
      return api.post(`/people/council/terms/${selectedTermId}/offices`, {
        memberId,
        roleCode: isCustom ? null : roleSelection,
        roleName: isCustom ? customRoleName.trim() : null,
      });
    },
    onSuccess: () => {
      toast.success(t('council.officeSaved'));
      closeOfficeDialog();
      void queryClient.invalidateQueries({
        queryKey: ['people', 'council', 'terms', selectedTermId, 'offices'],
      });
      void queryClient.invalidateQueries({ queryKey: ['people', 'council', 'terms'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const updateOffice = useMutation({
    mutationFn: () => {
      if (!editingOffice || !memberId || !roleSelection) {
        throw new Error('incomplete');
      }
      const isCustom = roleSelection === CUSTOM_ROLE;
      return api.patch(`/people/council/offices/${editingOffice.id}`, {
        memberId,
        roleCode: isCustom ? null : roleSelection,
        roleName: isCustom ? customRoleName.trim() : null,
      });
    },
    onSuccess: () => {
      toast.success(t('council.officeUpdated'));
      closeOfficeDialog();
      void queryClient.invalidateQueries({
        queryKey: ['people', 'council', 'terms', selectedTermId, 'offices'],
      });
      void queryClient.invalidateQueries({ queryKey: ['people', 'council', 'terms'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const removeOffice = useMutation({
    mutationFn: (officeId: string) => api.delete(`/people/council/offices/${officeId}`),
    onSuccess: () => {
      toast.success(t('council.officeRemoved'));
      setRemoveOfficeId(null);
      void queryClient.invalidateQueries({
        queryKey: ['people', 'council', 'terms', selectedTermId, 'offices'],
      });
      void queryClient.invalidateQueries({ queryKey: ['people', 'council', 'terms'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const reorderOffices = useMutation({
    mutationFn: (officeIds: string[]) => {
      if (!selectedTermId) throw new Error('missing term');
      return api.post<{ data: CouncilOffice[] }>(
        `/people/council/terms/${selectedTermId}/offices/reorder`,
        { officeIds },
      );
    },
    onSuccess: (result) => {
      queryClient.setQueryData(
        ['people', 'council', 'terms', selectedTermId, 'offices'],
        result,
      );
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const officeRows = offices.data?.data ?? [];

  const roleOptions = useMemo(
    () => [
      ...(roles.data?.data ?? []).map((role) => ({
        value: role.code,
        label: t(`council.roles.${role.code}`, { defaultValue: role.label }),
      })),
      { value: CUSTOM_ROLE, label: t('council.customRole') },
    ],
    [roles.data?.data, t],
  );

  const memberOptions = useMemo(
    () =>
      (members.data?.data ?? []).map((person) => ({
        id: person.id,
        label: `${person.firstName} ${person.lastName}`.trim(),
      })),
    [members.data?.data],
  );

  const columns: DataTableColumn<CouncilOffice>[] = [
    {
      id: 'role',
      header: t('council.columns.position'),
      cell: (row) => row.roleName,
    },
    {
      id: 'name',
      header: t('council.columns.person'),
      cell: (row) => <MemberNameLink memberId={row.memberId} name={row.memberName} />,
    },
    ...(canManage
      ? [
          {
            id: 'actions',
            header: tCommon('table.actions'),
            align: 'right' as const,
            cell: (row: CouncilOffice) => (
              <div className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(event) => {
                    event.stopPropagation();
                    openEditOffice(row);
                  }}
                >
                  {tCommon('action.edit')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(event) => {
                    event.stopPropagation();
                    setRemoveOfficeId(row.id);
                  }}
                >
                  {tCommon('action.remove')}
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  const selectedTerm = termRows.find((term) => term.id === selectedTermId) ?? null;
  const hasCurrentTerm = termRows.some((term) => term.isCurrent);
  const blockCurrentOnCreate = termOpen && !editingTermId && hasCurrentTerm;
  const canSaveOffice =
    canManage &&
    Boolean(memberId) &&
    Boolean(roleSelection) &&
    (roleSelection !== CUSTOM_ROLE || customRoleName.trim().length > 0) &&
    (isEditingOffice || Boolean(selectedTermId));

  return (
    <div>
      <LeadershipSubnav />
      <PageHeader
        title={t('council.title')}
        description={t('council.description')}
        tipId="members.council.purpose"
      />

      <div className="mt-6 flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label>{t('council.term')}</Label>
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-full min-w-[9rem] max-w-[12rem]">
              <Listbox
                value={selectedTermId}
                onChange={setSelectedTermId}
                options={termRows.map((term) => ({
                  value: term.id,
                  label: formatTermLabel(term, t('council.currentTermBadge')),
                  description:
                    term.label?.trim() && !term.isCurrent ? formatTermYears(term) : undefined,
                }))}
                placeholder={t('council.termPlaceholder')}
                emptyMessage={t('council.noTerms')}
              />
            </div>

            {!selectedTerm ? (
              <>
                <p className="min-w-0 flex-1 text-body-sm text-fg-secondary">
                  {t('council.emptyTerms')}
                </p>
                {canManage ? (
                  <Button variant="outline" onClick={() => setTermsManageOpen(true)}>
                    {t('council.manageTerms')}
                  </Button>
                ) : null}
              </>
            ) : (
              <>
                <h4 className="min-w-0 flex-1 text-body-sm font-medium text-fg">
                  {t('council.positionsTitle')}
                </h4>
                {canManage ? (
                  <div className="flex shrink-0 flex-wrap justify-end gap-2">
                    <Button variant="outline" onClick={() => setTermsManageOpen(true)}>
                      {t('council.manageTerms')}
                    </Button>
                    <Button variant="outline" onClick={openAssignOffice}>
                      {t('council.setPositions')}
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>

        {selectedTerm ? (
          <DataTable
            rows={officeRows}
            columns={columns}
            rowKey={(row) => row.id}
            loading={offices.isLoading}
            empty={
              <p className="text-body-sm text-fg-secondary">{t('council.emptyOffices')}</p>
            }
            reorder={
              canManage && selectedTermId
                ? {
                    handleLabel: tCommon('action.dragToReorder'),
                    disabled: reorderOffices.isPending,
                    onReorder: async (officeIds) => {
                      await reorderOffices.mutateAsync(officeIds);
                    },
                  }
                : undefined
            }
          />
        ) : null}
      </div>

      <LeadershipTermsManageDialog
        open={termsManageOpen}
        onOpenChange={setTermsManageOpen}
        terms={termRows}
        selectedTermId={selectedTermId}
        title={t('council.manageTerms')}
        emptyMessage={t('council.emptyTerms')}
        addTermLabel={t('council.addTerm')}
        editTermLabel={t('council.editTerm')}
        cancelLabel={tCommon('action.cancel')}
        closeLabel={tCommon('action.close')}
        tipId="members.leadership.term"
        formatTermLabel={(term) => formatTermLabel(term, t('council.currentTermBadge'))}
        formatTermYears={formatTermYears}
        onAddTerm={openCreateTermFromManage}
        onEditTerm={openEditTermFromManage}
      />

      <Dialog
        open={termOpen}
        onOpenChange={(open) => {
          if (!open) closeTermDialog();
          else setTermOpen(true);
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>
              {editingTermId ? t('council.editTerm') : t('council.addTerm')}
            </DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              saveTerm.mutate();
            }}
          >
            <DialogBody>
              <div className="flex flex-col gap-3">
                <Tip tipId="members.leadership.term" />
                <div className={`grid gap-3 ${termIsCurrent ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="council-term-start">{t('council.startYear')}</Label>
                    <Input
                      id="council-term-start"
                      type="number"
                      inputMode="numeric"
                      min={1900}
                      max={2100}
                      required
                      value={startYear}
                      onChange={(event) => setStartYear(event.target.value)}
                    />
                  </div>
                  {!termIsCurrent ? (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="council-term-end">{t('council.endYear')}</Label>
                      <Input
                        id="council-term-end"
                        type="number"
                        inputMode="numeric"
                        min={1900}
                        max={2100}
                        required
                        value={endYear}
                        onChange={(event) => setEndYear(event.target.value)}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="council-term-label">{t('council.termLabel')}</Label>
                  <Input
                    id="council-term-label"
                    value={termLabel}
                    onChange={(event) => setTermLabel(event.target.value)}
                    placeholder={t('council.termLabelPlaceholder')}
                  />
                </div>
                <SwitchField
                  id="council-term-current"
                  label={t('council.currentTermLabel')}
                  description={
                    blockCurrentOnCreate
                      ? t('council.currentTermAddBlockedHint')
                      : t('council.currentTermHint')
                  }
                  checked={termIsCurrent}
                  disabled={blockCurrentOnCreate}
                  onCheckedChange={(checked) => {
                    setTermIsCurrent(checked);
                    if (!checked) {
                      setEndYear((current) => current || startYear);
                    }
                  }}
                />
              </div>
            </DialogBody>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={closeTermDialog}>
                {tCommon('action.cancel')}
              </Button>
              <Button type="submit" loading={saveTerm.isPending}>
                {t('council.saveTerm')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={officeOpen}
        onOpenChange={(open) => {
          if (!open) closeOfficeDialog();
          else setOfficeOpen(true);
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>
              {isEditingOffice ? t('council.editOffice') : t('council.assign')}
            </DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (!canSaveOffice) return;
              if (isEditingOffice) updateOffice.mutate();
              else assignOffice.mutate();
            }}
          >
            <DialogBody>
              <div className="flex flex-col gap-3">
                {selectedTerm ? (
                  <p className="text-body-sm text-fg-secondary">
                    {isEditingOffice
                      ? t('council.editHint', {
                          term: selectedTerm.label?.trim() || formatTermYears(selectedTerm),
                        })
                      : t('council.assignHint', {
                          term: selectedTerm.label?.trim() || formatTermYears(selectedTerm),
                        })}
                  </p>
                ) : null}
                <div className="flex flex-col gap-1.5">
                  <Label>{t('council.position')}</Label>
                  <Listbox
                    value={roleSelection}
                    onChange={setRoleSelection}
                    options={roleOptions}
                    placeholder={t('council.positionPlaceholder')}
                    searchable
                    popoverPortal="dialog"
                  />
                </div>
                {roleSelection === CUSTOM_ROLE ? (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="council-custom-role">{t('council.customRoleName')}</Label>
                    <Input
                      id="council-custom-role"
                      value={customRoleName}
                      onChange={(event) => setCustomRoleName(event.target.value)}
                      required
                    />
                  </div>
                ) : null}
                <div className="flex flex-col gap-1.5">
                  <Label>{t('council.person')}</Label>
                  <EntityCombobox
                    value={memberId}
                    onChange={(id) => {
                      if (!id) {
                        setMemberId(null);
                        setSelectedMember(null);
                        return;
                      }
                      const option = memberOptions.find((item) => item.id === id) ?? null;
                      setMemberId(id);
                      setSelectedMember(option);
                    }}
                    options={memberOptions}
                    selected={selectedMember}
                    onQueryChange={setMemberQuery}
                    placeholder={t('council.personPlaceholder')}
                    emptyMessage={t('council.noPeople')}
                    loading={members.isFetching}
                    clearLabel={tCommon('action.clearSelection')}
                    popoverPortal="dialog"
                  />
                </div>
              </div>
            </DialogBody>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={closeOfficeDialog}>
                {tCommon('action.cancel')}
              </Button>
              <Button
                type="submit"
                loading={assignOffice.isPending || updateOffice.isPending}
                disabled={!canSaveOffice}
              >
                {isEditingOffice ? t('council.updateOffice') : t('council.saveOffice')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(removeOfficeId)}
        onOpenChange={(open) => {
          if (!open) setRemoveOfficeId(null);
        }}
        title={t('council.removeTitle')}
        description={t('council.removeDescription')}
        confirmLabel={tCommon('action.remove')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={removeOffice.isPending}
        onConfirm={() => {
          if (removeOfficeId) removeOffice.mutate(removeOfficeId);
        }}
      />
    </div>
  );
};
